import { ConfigurationType } from '@server/parser';
import { ParsedFieldsByObject } from '../../reducers/global/globalReducerTypes';
import {
  ApexClassProperties,
  BaseFieldProperties,
  ApexTriggerProperties,
  ApprovalProcessProperties,
  BaseElementProperties,
  CpqRecordProperties,
  FlowProperties,
  PathProperties,
  ValidationRuleProperties,
  WorkflowRuleProperties,
  RecordTypeProperties,
  AuditData,
  FlowTypes,
} from '@server/documentation';

// ******* BE types *******
export interface OpenAiResponse {
  data: string;
  error: string;
}

export const LAYOUT = 'Layout';
export type RelatedListData = { objectName: string; name: string; label: string };
export type NameProperties = { name: string; label: string };

export type LayoutParserProperties = BaseElementProperties & {
  type: typeof LAYOUT;
  objectName: string;
  buttons: NameProperties[];
  relatedLists: RelatedListData[];
}; // TODO - align with LayoutProperties - this includes the merged salesforce data for buttons and related lists, while LayoutProperties only includes salto data

export interface ObjectData {
  name: string;
  visible: boolean;
  recordTypes: RecordTypeParserProperties[];
  fields?: BaseFieldProperties[];
  validationRules?: ValidationRuleProperties[];
  approvalProcesses?: ApprovalProcessProperties[];
  workflowRules?: WorkflowRuleProperties[];
  processBuilderFlows?: FlowProperties[];
  flows?: FlowProperties[];
  paths?: PathProperties[];
  apexTriggers?: ApexTriggerProperties[];
  apexClasses?: ApexClassProperties[];
  cpqData?: CpqRecordProperties[];
  layouts?: LayoutParserProperties[];
  relatedObjects?: {
    [objectName: string]: RelatedObjectParsedData;
  };
}

export type RelatedObjectParsedData = {
  [ConfigurationType.apexClasses]?: ApexClassProperties[];
  [ConfigurationType.fields]: BaseFieldProperties[];
  [ParserBaseRuleType.apexTriggers]?: ApexTriggerProperties[];
  [ParserBaseRuleType.validationRules]?: ValidationRuleProperties[];
  [ParserBaseRuleType.approvalProcesses]?: ApprovalProcessProperties[];
  [ParserBaseRuleType.workflowRules]?: WorkflowRuleProperties[];
  [ParserBaseRuleType.processBuilderFlows]?: FlowProperties[];
  [ParserBaseRuleType.flows]?: FlowProperties[];
  [ParserBaseRuleType.layouts]?: LayoutParserProperties[];
};

export type RecordTypeParserProperties = RecordTypeProperties & {
  leadingFields: LeadingFieldData[];
  attributions?: Attributions;
};

export interface LeadingFieldData {
  name: string;
  label: string;
  values: LeadingFieldValue[];
  hasPath: boolean;
  isLeadingCandidate?: boolean;
}

export type SfdcLeadStatusMetadata = {
  converted: boolean;
};

export type SfdcCaseStatusMetadata = {
  closed: boolean;
};

export enum OpportunityStageMetadataForecastCategory {
  COMMIT = 'Forecast', // label is Commit, value is Forecast
  OMITTED = 'Omitted',
  PIPELINE = 'Pipeline',
  BEST_CASE = 'BestCase', // label is `Best Case` value is BestCase
  CLOSED = 'Closed',
  MOST_LIKELY = 'MostLikely',
}

export type SfdcOpportunityStageMetadata = {
  won: boolean;
  closed: boolean;
  probability: number;
  forecastCategory: OpportunityStageMetadataForecastCategory;
};

export type SfdcStageMetadata =
  | SfdcCaseStatusMetadata
  | SfdcLeadStatusMetadata
  | SfdcOpportunityStageMetadata;

export type StageMetadataOptions = Partial<
  SfdcLeadStatusMetadata & SfdcCaseStatusMetadata & SfdcOpportunityStageMetadata
>;

export type LeadingFieldValue = NameProperties & {
  metadata?: { default: boolean } & StageMetadataOptions;
  [ParserBaseRuleType.validationRules]?: string[];
  [ParserBaseRuleType.flows]?: string[];
  [ParserBaseRuleType.processBuilderFlows]?: string[];
  [ParserBaseRuleType.workflowRules]?: string[];
  [ParserBaseRuleType.approvalProcesses]?: string[];
};

// ----- client types -----
export interface RelatedObjectData {
  parsedRules: ParsedRule[];
  parsedRecordTypes?: ParsedRecordType[];
  parsedFields?: ParsedFieldsByObject;
}

export interface RelatedObjectsMap {
  [objectName: string]: RelatedObjectData;
}

type Attributions = { [key in ConfigurationType]?: string[] };

type SFDCPicklistField = { name: string; label: string; values: SaltoPicklistValue[] };
export type SFDCLeadingField = SFDCPicklistField & { isLeadingCandidate: boolean };

export type FieldValue = {
  name: string;
  label: string;
  validationRules?: string[];
  flows?: string[];
  workflowRules?: string[];
  approvalProcesses?: string[];
  processBuilderFlows?: string[];
  apexTriggers?: string[]; //preparation for when supported by BE
  paths?: string[]; //preparation for when supported by BE
  cpqData?: string[];
  layouts?: string[];
};

export enum ParserBaseRuleType {
  validationRules = 'validationRules',
  approvalProcesses = 'approvalProcesses',
  workflowRules = 'workflowRules',
  flows = 'flows',
  processBuilderFlows = 'processBuilderFlows',
  apexTriggers = 'apexTriggers',
  apexClasses = 'apexClasses',
  cpqData = 'cpqData',
  layouts = 'layouts',
}

export type OpenAiDescription = {
  type: 'error' | 'data';
  text: string;
};

export type ParsedRule = {
  id: string;
  type: ParserBaseRuleType;
  name: string;
  label: string;
  description?: string;
  formula?: string;
  objectApiNames: string[];
  isUsedByRecordType?: boolean;
  usedByRecordTypes?: string[];
  stagesNames?: string[];
  annotations?: AuditData;
  link?: string;
  flowType?: FlowTypes; //relevant for "flow" ruleType
  flowDescription?: string; //relevant for "flow" ruleType
  errorMessage?: string; //relevant for validation rules
  filename: string;
  metadataId: string;
  isActive: boolean;
  referencedObjects?: string[];
  namespace?: string;
  processType?: string;
};

export type ParsedRecordType = {
  label: string;
  name: string;
  id: string;
  objectApiName: string;
  description?: string;
  leadingFields: LeadingFieldData[];
  attributions?: Attributions;
  annotations?: AuditData;
  link?: string;
  withHideButton?: boolean;
  filename: string;
  metadataId: string;
  isActive: boolean;
  namespace?: string;
  type?: string;
};

//This type is for the API call: POST /parser  (the BE should unite the objectType types....)
export type ObjectType = {
  name: string;
  label?: string; //TBD if we need it here as we're displaying SF objects instead
  visible: boolean;
};

export type ParserItemsMap = {
  [type in ConfigurationType]: string[];
};
